// Raw design tokens — not mode-aware. These are the base palette scales.

export const rawColors = {
  // Blue scale (primary)
  blue50: '#eff6ff',
  blue100: '#dbeafe',
  blue200: '#bfdbfe',
  blue300: '#93c5fd',
  blue500: '#3b82f6',
  blue600: '#2563eb',
  blue700: '#1d4ed8',
  blue800: '#1e40af',
  blue900: '#1e3a8a',

  // Gray scale
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray800: '#1f2937',
  gray900: '#111827',
  gray950: '#030712',

  // Green scale
  green50: '#ecfdf5',
  green100: '#dcfce7',
  green200: '#a7f3d0',
  green400: '#22c55e',
  green500: '#10b981',
  green600: '#059669',
  green700: '#047857',
  green800: '#065f46',
  green900: '#166534',

  // Red scale
  red50: '#fef2f2',
  red100: '#fee2e2',
  red200: '#fecaca',
  red400: '#f87171',
  red500: '#ef4444',
  red600: '#dc2626',
  red700: '#b91c1c',
  red800: '#991b1b',

  // Amber scale
  amber50: '#fffbeb',
  amber100: '#fef3c7',
  amber200: '#fde68a',
  amber400: '#fbbf24',
  amber500: '#f59e0b',
  amber800: '#92400e',
  amber900: '#78350f',

  // Purple scale (AI palette)
  purple50: '#faf5ff',
  purple100: '#f5f3ff',
  purple200: '#e9d5ff',
  purple300: '#ddd6fe',
  purple400: '#a78bfa',
  purple500: '#7c3aed',
  purple600: '#6b21a8',
  purple700: '#6d28d9',
  purple800: '#5b21b6',

  // Indigo scale
  indigo100: '#e0e7ff',
  indigo800: '#3730a3',

  // Sky scale (info)
  sky50: '#f0f9ff',
  sky200: '#bae6fd',
  sky700: '#0369a1',

  // Yellow
  yellow200: '#fef08a',

  // Fixed
  white: '#ffffff',
  black: '#000000',
} as const;

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  xxxl: 32,
} as const;

export const radius = {
  sm: 6,
  md: 8,
  lg: 10,
  xl: 12,
  xxl: 16,
  full: 9999,
} as const;

export const shadows = {
  sm: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 4,
    elevation: 2,
  },
  md: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 4,
  },
  fab: {
    shadowColor: rawColors.blue500,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
} as const;

export const FONT_SCALE_VALUES = {
  small: 0.85,
  default: 1.0,
  large: 1.15,
  xlarge: 1.3,
} as const;

export const typography = {
  // Size scale
  xs: { fontSize: 11 as number },
  sm: { fontSize: 13 as number },
  base: { fontSize: 15 as number },
  md: { fontSize: 16 as number },
  lg: { fontSize: 18 as number },
  xl: { fontSize: 20 as number },
  '2xl': { fontSize: 24 as number },
  '3xl': { fontSize: 32 as number },
  '4xl': { fontSize: 40 as number },
  display: { fontSize: 64 as number },

  // Weight presets
  regular: { fontWeight: '400' as const },
  medium: { fontWeight: '500' as const },
  semibold: { fontWeight: '600' as const },
  bold: { fontWeight: '700' as const },
  extrabold: { fontWeight: '800' as const },

  // Composite presets
  heading1: { fontSize: 24 as number, fontWeight: '700' as const, lineHeight: 32 as number },
  heading2: { fontSize: 20 as number, fontWeight: '700' as const, lineHeight: 28 as number },
  heading3: { fontSize: 18 as number, fontWeight: '700' as const },
  body: { fontSize: 15 as number, lineHeight: 22 as number },
  bodySmall: { fontSize: 13 as number, lineHeight: 18 as number },
  caption: { fontSize: 11 as number, fontWeight: '600' as const, letterSpacing: 0.5 },
  label: { fontSize: 12 as number, fontWeight: '700' as const, letterSpacing: 0.5, textTransform: 'uppercase' as const },
  button: { fontSize: 16 as number, fontWeight: '700' as const },
  sectionLabel: { fontSize: 14 as number, fontWeight: '700' as const, textTransform: 'uppercase' as const, letterSpacing: 0.5 },
} as const;

export function createScaledTypography(scale: number): typeof typography {
  if (scale === 1.0) return typography;
  const s = (size: number) => Math.round(size * scale);
  return {
    ...typography,
    xs: { fontSize: s(11) },
    sm: { fontSize: s(13) },
    base: { fontSize: s(15) },
    md: { fontSize: s(16) },
    lg: { fontSize: s(18) },
    xl: { fontSize: s(20) },
    '2xl': { fontSize: s(24) },
    '3xl': { fontSize: s(32) },
    '4xl': { fontSize: s(40) },
    display: { fontSize: s(64) },
    heading1: { ...typography.heading1, fontSize: s(24), lineHeight: s(32) },
    heading2: { ...typography.heading2, fontSize: s(20), lineHeight: s(28) },
    heading3: { ...typography.heading3, fontSize: s(18) },
    body: { ...typography.body, fontSize: s(15), lineHeight: s(22) },
    bodySmall: { ...typography.bodySmall, fontSize: s(13), lineHeight: s(18) },
    caption: { ...typography.caption, fontSize: s(11) },
    label: { ...typography.label, fontSize: s(12) },
    button: { ...typography.button, fontSize: s(16) },
    sectionLabel: { ...typography.sectionLabel, fontSize: s(14) },
  };
}
